import React from 'react';
import dynamic from 'next/dynamic';
import { submitForm } from '@services/formService';
import { extractColors, formatHttpsUrl } from '@utils/utils';
import { getCtaProperties } from '@utils/ctaUtils';

const CTA = dynamic(() => import('@components/Shared/CTA/CTA'), {
  ssr: false,
});

const EmbeddedVideo = dynamic(
  () => import('@components/Shared/EmbeddedVideo/EmbeddedVideo'),
  {
    ssr: false,
  }
);

const CustomerTestimonial = dynamic(
  () => import('@components/Shared/CustomerTestimonial/CustomerTestimonial'),
  {
    ssr: false,
  }
);

const ContentfulWebForm = dynamic(
  () => import('@components/Shared/ContentfulWebForm/ContentfulWebForm'),
  {
    ssr: false,
  }
);

export const renderDynamicContentPage = (contentfulData) => {
  if (typeof contentfulData === 'string' && contentfulData === '') {
    console.error(
      'Warning: Contentful Data not found. Check to ensure the ID is correct or Contentful is not down!'
    );
    return {};
  }

  const result = {};
  result.mobileHeroImages = contentfulData.mobileHeroImage
    ? contentfulData.mobileHeroImage.map((hero) => {
        const mediaUrl =
          hero.media !== null ? formatHttpsUrl(hero.media.file.url) : null;
        return (
          // eslint-disable-next-line react/jsx-key
          <img
            className="hero-image hero-image-mobile"
            src={mediaUrl}
            alt={hero.altText}
          />
        );
      })
    : null;

  result.heroImages = contentfulData.heroImage
    ? contentfulData.heroImage.map((hero) => {
        //Only taking the first mobile hero image for now.
        const mobileHero =
          result.mobileHeroImages !== null ? result.mobileHeroImages[0] : null;
        const mediaUrl =
          hero.media !== null ? formatHttpsUrl(hero.media.file.url) : null;
        return (
          <div
            key={hero.sys.id}
            className="dynamic-hero-images constrained-width"
          >
            {mobileHero}
            <img
              className="hero-image hero-image-desktop"
              src={mediaUrl}
              alt={hero.altText}
            />
          </div>
        );
      })
    : null;

  result.desktopHeroImageSources = contentfulData.heroImage
    ? contentfulData.heroImage.map((hero) => {
        return hero.media !== null ? formatHttpsUrl(hero.media.file.url) : null;
      })
    : null;

  result.mobileHeroImageSources = contentfulData.heroImage
    ? contentfulData.mobileHeroImage.map((hero) => {
        return hero.media !== null ? formatHttpsUrl(hero.media.file.url) : null;
      })
    : null;

  result.contentCTAs = contentfulData.contentCTAs
    ? contentfulData.contentCTAs.map((cta) => {
        return (
          <div className="cta" key={cta.sys.id}>
            <CTA
              buttonLocation={cta.ctaButtonLocation}
              buttonColors={extractColors(cta.buttonAndTextColors)}
              secondButtonColors={cta.secondButtonColor}
              backgroundColor={extractColors(cta.ctaBackgroundColor)}
              image={
                cta.ctaImage !== null
                  ? formatHttpsUrl(cta.ctaImage.file.url)
                  : null
              }
              imageAlign={cta.ctaImageLocation}
              contentAlign={cta.alignCtaContent}
              customCategory={cta.ctaCustomCategory}
              tag={cta.ctaTag}
              headingText={cta.ctaHeader}
              subHeadingText={cta.ctaSubHeader}
              ctaText={cta.ctaIntro}
              buttonText={cta.firstCtaButtonText}
              buttonUrl={cta.firstCtaButtonUrl}
              buttonAutomationId="cta-button-1"
              button2Text={cta.secondCtaButtonText}
              button2Url={cta.secondCtaButtonUrl}
              button2AutomationId="cta-button-2"
              buttonOpensToNewTab={true}
            />
          </div>
        );
      })
    : null;

  result.contentCTAProps = contentfulData.contentCTAs
    ? contentfulData.contentCTAs.map((cta) => {
        return getCtaProperties(cta);
      })
    : null;

  result.contentVideos = contentfulData.contentVideos
    ? contentfulData.contentVideos.map((video) => {
        return (
          <EmbeddedVideo
            key={video.sys.id}
            videoSource={formatHttpsUrl(video.videoSource)}
            childMarkdownRemark={video.embeddedContent}
            altText={video.embeddedContentHeader}
          />
        );
      })
    : null;

  result.testimonials = contentfulData.customerTestimonials
    ? contentfulData.customerTestimonials.map((testimonial) => {
        return (
          <CustomerTestimonial
            key={testimonial.sys.id}
            testimonial={testimonial}
          />
        );
      })
    : null;

  result.prefooterCTA =
    contentfulData.preFooterCTA !== null ? (
      <div className="prefooter-cta cta">
        <CTA
          isPreFooterCTA={true}
          backgroundColor={extractColors(
            contentfulData.preFooterCTA.ctaBackgroundColor
          )}
          buttonColors={extractColors(
            contentfulData.preFooterCTA.buttonAndTextColors
          )}
          image={formatHttpsUrl(contentfulData.preFooterCTA.ctaImage.file.url)}
          imageAlign={contentfulData.preFooterCTA.ctaImageLocation}
          contentAlign={contentfulData.preFooterCTA.alignCtaContent}
          customCategory={contentfulData.preFooterCTA.ctaCustomCategory}
          tag={contentfulData.preFooterCTA.ctaTag}
          headingText={contentfulData.preFooterCTA.ctaHeader}
          subHeadingText={contentfulData.preFooterCTA.ctaSubHeader}
          ctaText={contentfulData.preFooterCTA.ctaIntro}
          buttonText={contentfulData.preFooterCTA.firstCtaButtonText}
          buttonUrl={contentfulData.preFooterCTA.firstCtaButtonUrl}
          buttonAutomationId="dynamic-prefooter-cta-2"
          button2Text={contentfulData.preFooterCTA.secondCtaButtonText}
          button2Url={contentfulData.preFooterCTA.secondCtaButtonUrl}
          button2AutomationId="cta-button-2"
        />
      </div>
    ) : null;

  result.webForms = contentfulData.webForms
    ? contentfulData.webForms.map((formInfo) => {
        const redirectUrl =
          formInfo.firstFormButtonURL !== null &&
          typeof formInfo.firstFormButtonURL !== 'undefined' &&
          formInfo.firstFormButtonURL.startsWith('http')
            ? formInfo.firstFormButtonURL
            : '/' + formInfo.firstFormButtonURL;
        const redirectUrl2 =
          formInfo.secondFormButtonURL !== null &&
          typeof formInfo.secondFormButtonURL !== 'undefined' &&
          formInfo.secondFormButtonURL.startsWith('http')
            ? formInfo.secondFormButtonURL
            : '/' + formInfo.secondFormButtonURL;
        const formColors = extractColors(formInfo.buttonAndTextColors);
        return (
          <ContentfulWebForm
            key={formInfo.dataExtension}
            buttonText={formInfo.firstFormButtonText}
            buttonColor={formColors[0]}
            buttonTextColor={formColors[1]}
            button2Color={formInfo.secondButtonColor}
            button2Text={formInfo.secondFormButtonText}
            formContactFields={formInfo.formContactFields}
            headerIcon={formInfo.headerIcon}
            headerColor={formInfo.headerColor}
            headerText={formInfo.formHeader}
            subheaderText={formInfo.formSubHeader}
            formText={formInfo.formIntro}
            showDisclaimer={formInfo.disclaimerCheckbox}
            disclaimerText={formInfo.disclaimer}
            redirectUrl={redirectUrl}
            redirectUrl2={redirectUrl2}
            dataExtension={formInfo.dataExtension}
            originWebFormType={formInfo.originWebFormType}
            originWebApp={formInfo.originWebApp}
            originWebPageType={formInfo.originWebPageType}
            originCampaign={formInfo.originCampaign}
            originWebFormCategory={formInfo.originWebFormCategory}
            originWebFormCategory2={formInfo.secondOriginWebFormCategory}
            customGAEventClass="form-submit-chcom"
            submitForm={submitForm}
          />
        );
      })
    : null;

  result.heroCTA =
    contentfulData.heroCTA !== null ? (
      <div className="hero-cta cta">
        <div className="cta-container">
          <CTA
            backgroundColor={extractColors(
              contentfulData.heroCTA.ctaBackgroundColor
            )}
            buttonColors={extractColors(
              contentfulData.heroCTA.buttonAndTextColors
            )}
            image={formatHttpsUrl(contentfulData.heroCTA.ctaImage.file.url)}
            imageAlign={contentfulData.heroCTA.ctaImageLocation}
            contentAlign={contentfulData.heroCTA.ctaContentAlign}
            customCategory={contentfulData.heroCTA.ctaCustomCategory}
            tag={contentfulData.heroCTA.ctaTag}
            headingText={contentfulData.heroCTA.ctaHeader}
            subHeadingText={contentfulData.heroCTA.ctaSubHeader}
            ctaText={contentfulData.heroCTA.ctaIntro}
            buttonText={contentfulData.heroCTA.firstCtaButtonText}
            buttonUrl={contentfulData.heroCTA.firstCtaButtonUrl}
            buttonAutomationId="dynamic-hero-cta"
            button2Text={contentfulData.heroCTA.secondCtaButtonText}
            button2Url={contentfulData.heroCTA.secondCtaButtonUrl}
            button2AutomationId="dynamic-hero-cta-2"
          />
        </div>
      </div>
    ) : null;

  return result;
};
